// src/components/compliance/LexKollerModal.tsx
/**
 * Lex Koller assessment.
 *
 * Steps: (institutional pre-check) → entity type → nationality → permit /
 * residence → result. Every question we can already answer from the
 * identification or from Zefix is skipped (Issue #1000, Wiki rule R5).
 *
 * Renders either as its own dialog (settings entry point) or, with `embedded`,
 * as a section inside the identification dialog — one dialog, two steps.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  Shield,
  Building2,
  User,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Loader2,
  X,
  Globe,
  MapPin,
  FileCheck,
  Landmark,
} from 'lucide-react'
import complianceApi from '@/services/compliance'
import {
  ENTITY_TYPE,
  LEX_KOLLER_SOURCE_KYC_AI,
  LEX_KOLLER_STATUS,
  NATIONALITY_CATEGORY,
  NATIONALITY_CATEGORIES,
  RESIDENCE_PERMITS,
  SWISS_CANTONS,
  nationalityCategoryFromCode,
} from '@/constants/compliance'
import type {
  EntityType,
  LexKollerAssessmentRequest,
  LexKollerAssessmentResponse,
  LexKollerData,
  LexKollerPrecheck,
  LexKollerStatusResponse,
  NationalityCategory,
  ResidencePermit,
} from '@/types/compliance'
import type { KycStatusResponse } from '@/types/kyc'

interface Props {
  isOpen: boolean
  onClose: () => void
  onComplete: (status: string) => void
  /**
   * Optional "remind me later" handler. When set, an explicit skip button is shown
   * (auto-opened dialog); omit it where the assessment is started deliberately.
   */
  onSkip?: () => void
  /** Previous answers; when omitted they are read from the API on open. */
  initialData?: LexKollerData | null
  /**
   * Identification status. Whatever it already answers (company from the
   * contract, nationality from the ident, Swiss domicile from Zefix) is
   * pre-filled and no longer asked.
   */
  kycStatus?: KycStatusResponse | null
  /** Render inline inside another dialog instead of as an own modal. */
  embedded?: boolean
}

type Step = 'auto' | 'institutional' | 'entity' | 'nationality' | 'details' | 'result'

/**
 * True when the assessment on file was derived from the identity document
 * (Issue #1044): our AI read `CHE`, so the person is a Swiss citizen and
 * nothing is left to ask.
 */
function isAutoSwiss(status: LexKollerStatusResponse): boolean {
  const source = (status.data as { source?: unknown } | null)?.source
  return status.status === LEX_KOLLER_STATUS.UNRESTRICTED && source === LEX_KOLLER_SOURCE_KYC_AI
}

/** Reads the stored assessment (`lex_koller_data`) back into typed answers. */
function toLexKollerData(raw?: Record<string, unknown> | null): LexKollerData | null {
  if (!raw) return null
  const entityType = raw.entity_type
  const nationality = raw.nationality
  const permit = raw.residence_permit
  return {
    entity_type: entityType === ENTITY_TYPE.PERSON || entityType === ENTITY_TYPE.COMPANY
      ? (entityType as EntityType)
      : undefined,
    nationality: NATIONALITY_CATEGORIES.includes(nationality as NationalityCategory)
      ? (nationality as NationalityCategory)
      : undefined,
    residence_permit: RESIDENCE_PERMITS.includes(permit as ResidencePermit)
      ? (permit as ResidencePermit)
      : null,
    swiss_residence: typeof raw.swiss_residence === 'boolean' ? raw.swiss_residence : undefined,
    swiss_canton: typeof raw.swiss_canton === 'string' ? raw.swiss_canton : null,
    company_domicile_ch:
      typeof raw.company_domicile_ch === 'boolean' ? raw.company_domicile_ch : null,
    foreign_ownership_above_33pct:
      typeof raw.foreign_ownership_above_33pct === 'boolean'
        ? raw.foreign_ownership_above_33pct
        : null,
  }
}

export default function LexKollerModal({
  isOpen,
  onClose,
  onComplete,
  onSkip,
  initialData,
  kycStatus,
  embedded = false,
}: Props) {
  const { t } = useTranslation()
  const [step, setStep] = useState<Step>('entity')
  const [loading, setLoading] = useState(false)
  const [statusLoading, setStatusLoading] = useState(true)

  // Pre-check against the KYC whitelist (FINMA/OAK/SIX)
  const [precheck, setPrecheck] = useState<LexKollerPrecheck | null>(null)
  const [noForeignControl, setNoForeignControl] = useState(false)
  const [institutionalDone, setInstitutionalDone] = useState(false)

  // Form state
  const [entityType, setEntityType] = useState<EntityType | null>(null)
  const [nationality, setNationality] = useState<NationalityCategory | null>(null)
  const [residencePermit, setResidencePermit] = useState<ResidencePermit | null>(null)
  const [swissResidence, setSwissResidence] = useState<boolean | null>(null)
  const [swissCanton, setSwissCanton] = useState<string>('')
  const [companyDomicileCh, setCompanyDomicileCh] = useState<boolean | null>(null)
  const [foreignOwnership, setForeignOwnership] = useState<boolean | null>(null)

  // Which answers came from the identification — those questions are skipped
  const [entityPrefilled, setEntityPrefilled] = useState(false)
  const [nationalityPrefilled, setNationalityPrefilled] = useState(false)
  const [domicilePrefilled, setDomicilePrefilled] = useState(false)
  // Nationality taken over from the read document — asked, but pre-selected
  const [nationalityFromKyc, setNationalityFromKyc] = useState(false)

  // Where the questionnaire starts, should the automatic result be edited
  const [startStep, setStartStep] = useState<Step>('entity')

  const [result, setResult] = useState<LexKollerAssessmentResponse | null>(null)

  const seededRef = useRef(false)

  /** Pre-fills the form and opens on the first question that is still unanswered. */
  const seed = useCallback(
    (
      check: LexKollerPrecheck | null,
      previous: LexKollerData | null,
      /** Nationality of the identification as ISO-3 (`kyc_nationality`, #1044). */
      kycNationality?: string | null,
      /** Swiss citizenship already derived from the document — hint instead of form. */
      autoSwiss = false,
    ) => {
      const identNationality = nationalityCategoryFromCode(kycStatus?.contact?.nationality)
      const kycNationalityCategory = nationalityCategoryFromCode(kycNationality)
      const identCompany = kycStatus?.company ?? null
      // A previous assessment stays fully editable — nothing is skipped then.
      const isEdit = !!previous?.entity_type

      const seededEntity = previous?.entity_type ?? (identCompany ? ENTITY_TYPE.COMPANY : null)
      // The document only pre-selects the answer, it never replaces the
      // question — «DEU» says nothing about permit or residence.
      const seededNationality = previous?.nationality ?? identNationality ?? kycNationalityCategory
      const seededDomicile =
        previous?.company_domicile_ch ?? (identCompany?.zefix_verified ? true : null)

      setEntityType(seededEntity)
      setNationality(seededNationality)
      setResidencePermit(previous?.residence_permit ?? null)
      setSwissResidence(previous?.swiss_residence ?? null)
      setSwissCanton(previous?.swiss_canton ?? '')
      setCompanyDomicileCh(seededDomicile)
      setForeignOwnership(previous?.foreign_ownership_above_33pct ?? null)

      const knownEntity = !isEdit && !!identCompany
      const knownNationality = !isEdit && !!identNationality
      setEntityPrefilled(knownEntity)
      setNationalityPrefilled(knownNationality)
      setDomicilePrefilled(!isEdit && !!identCompany?.zefix_verified)
      setNationalityFromKyc(
        !previous?.nationality && !identNationality && !!kycNationalityCategory,
      )

      setNoForeignControl(false)
      setInstitutionalDone(false)
      setResult(null)

      const start: Step = check?.institutional
        ? 'institutional'
        : !knownEntity
          ? 'entity'
          : !knownNationality
            ? 'nationality'
            : 'details'
      setStartStep(start)
      setStep(autoSwiss ? 'auto' : start)
    },
    [kycStatus],
  )

  // Load the pre-check (and the previous answers, if the caller has none) as
  // soon as the assessment is opened.
  useEffect(() => {
    if (!isOpen) {
      seededRef.current = false
      return
    }
    if (seededRef.current) return
    seededRef.current = true

    let cancelled = false
    setStatusLoading(true)
    complianceApi
      .getLexKollerStatus()
      .then((res) => {
        if (cancelled) return
        setPrecheck(res.precheck ?? null)
        const autoSwiss = isAutoSwiss(res)
        seed(
          res.precheck ?? null,
          initialData ?? toLexKollerData(res.data),
          res.kyc_nationality,
          autoSwiss,
        )
        // The assessment already exists on the server — let the caller know so
        // its copy of the status does not stay on «not assessed».
        if (autoSwiss && res.status) onComplete(res.status)
      })
      .catch(() => {
        // Status unavailable — fall back to the full questionnaire
        if (cancelled) return
        setPrecheck(null)
        seed(null, initialData ?? null)
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  /**
   * The questions still to answer, in order.
   *
   * A company is always asked for its ownership, a Swiss private person never
   * for permit and residence.
   */
  const needsDetails = entityType === ENTITY_TYPE.COMPANY || nationality !== NATIONALITY_CATEGORY.CH
  const questionSteps: Step[] = [
    ...(precheck?.institutional ? (['institutional'] as Step[]) : []),
    ...(entityPrefilled ? [] : (['entity'] as Step[])),
    ...(nationalityPrefilled ? [] : (['nationality'] as Step[])),
    ...(needsDetails ? (['details'] as Step[]) : []),
  ]

  const handleSubmit = async () => {
    if (!entityType || !nationality) return
    setLoading(true)
    try {
      const req: LexKollerAssessmentRequest = {
        entity_type: entityType,
        nationality,
        residence_permit: residencePermit,
        swiss_residence: swissResidence ?? false,
        swiss_canton: swissCanton || null,
        company_domicile_ch: companyDomicileCh,
        foreign_ownership_above_33pct: foreignOwnership,
      }
      const res = await complianceApi.assessLexKoller(req)
      setResult(res)
      setStep('result')
      onComplete(res.status)
    } catch {
      toast.error(
        t('lexKoller.errors.assessFailed', 'Die Einschätzung konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.'),
      )
    } finally {
      setLoading(false)
    }
  }

  /** Institutional counterparty: one confirmation instead of the questionnaire. */
  const handleDeclareInstitutional = async () => {
    setLoading(true)
    try {
      const res = await complianceApi.declareLexKollerInstitutional({
        no_foreign_control_above_33pct: true,
      })
      setInstitutionalDone(true)
      setStep('result')
      onComplete(res.status)
    } catch {
      toast.error(
        t('lexKoller.errors.declareFailed', 'Die Bestätigung konnte nicht gespeichert werden. Bitte versuchen Sie es später erneut.'),
      )
    } finally {
      setLoading(false)
    }
  }

  const goNext = () => {
    if (step === 'institutional' && noForeignControl) {
      handleDeclareInstitutional()
      return
    }
    const next = questionSteps[questionSteps.indexOf(step) + 1]
    if (next) setStep(next)
    else handleSubmit()
  }

  const previousStep = (): Step | null => questionSteps[questionSteps.indexOf(step) - 1] ?? null

  const goBack = () => {
    const previous = previousStep()
    if (previous) setStep(previous)
  }

  const canProceed = () => {
    // Confirming is optional here — «Weiter» leads into the questionnaire
    if (step === 'institutional') return true
    if (step === 'entity') return entityType !== null
    if (step === 'nationality') return nationality !== null
    if (step === 'details') {
      if (entityType === ENTITY_TYPE.COMPANY) {
        if (companyDomicileCh === null) return false
        // Foreign control decides the assessment — never submit it unanswered
        return companyDomicileCh === false || foreignOwnership !== null
      }
      return residencePermit !== null && swissResidence !== null
    }
    return false
  }

  const isPositive = (status: string) =>
    status === LEX_KOLLER_STATUS.UNRESTRICTED ||
    status === LEX_KOLLER_STATUS.NOT_APPLICABLE_INSTITUTIONAL

  const statusIcon = (status: string) => {
    if (isPositive(status)) return <CheckCircle2 className="h-12 w-12 text-emerald-500" />
    if (status === LEX_KOLLER_STATUS.CONDITIONAL) return <AlertTriangle className="h-12 w-12 text-amber-500" />
    return <XCircle className="h-12 w-12 text-red-500" />
  }

  const statusColor = (status: string) => {
    if (isPositive(status)) return 'bg-emerald-50 border-emerald-200'
    if (status === LEX_KOLLER_STATUS.CONDITIONAL) return 'bg-amber-50 border-amber-200'
    return 'bg-red-50 border-red-200'
  }

  const institutionalCompany =
    precheck?.company_name || t('lexKoller.institutional.yourCompany', 'Ihre Gesellschaft')
  const institutionalSource =
    precheck?.regulated_source || t('lexKoller.institutional.unknownSource', 'öffentliches Register')

  const OptionCard = ({
    selected,
    onClick,
    icon,
    title,
    description,
  }: {
    selected: boolean
    onClick: () => void
    icon: React.ReactNode
    title: string
    description: string
  }) => (
    <button
      data-testid="lex-koller-modal-click-btn"
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
        selected
          ? 'border-primary-500 bg-primary-50 shadow-sm'
          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
      }`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${selected ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-500'}`}>
          {icon}
        </div>
        <div className="min-w-0">
          <p className={`font-semibold ${selected ? 'text-primary-700' : 'text-gray-900'}`}>{title}</p>
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        </div>
      </div>
    </button>
  )

  const body = (
    <div className={embedded ? 'space-y-5' : 'px-4 sm:px-6 py-5 space-y-5'}>
      {statusLoading ? (
        <div data-testid="lex-koller-modal-loading" className="flex justify-center py-8">
          <Loader2 size={24} className="animate-spin text-primary-500" />
        </div>
      ) : (
        <>
          {/* Progress dots — only over the questions that are actually asked */}
          {step !== 'result' && step !== 'auto' && questionSteps.length > 1 && (
            <div className="flex items-center justify-center gap-2">
              {questionSteps.map((s, i) => (
                <div
                  key={s}
                  className={`h-2 rounded-full transition-all ${
                    s === step
                      ? 'w-8 bg-primary-500'
                      : i < questionSteps.indexOf(step)
                        ? 'w-2 bg-primary-300'
                        : 'w-2 bg-gray-200'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Swiss citizenship from the identity document — no questionnaire */}
          {step === 'auto' && (
            <div data-testid="lex-koller-modal-auto" className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50">
                <CheckCircle2 size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    {t(
                      'lexKoller.auto.title',
                      'Automatisch ermittelt: Schweizer Staatsbürger — keine Einschränkung',
                    )}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t(
                      'lexKoller.auto.description',
                      'Ihre Staatsangehörigkeit stammt aus Ihrem Ausweis. Lex Koller schränkt Sie damit nicht ein — Sie müssen nichts ausfüllen.',
                    )}
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-400 text-center">
                {t('lexKoller.disclaimer', 'Dies ist eine informative Einschätzung und keine Rechtsberatung. Für verbindliche Auskünfte wenden Sie sich an einen Notar oder Rechtsanwalt.')}
              </p>
            </div>
          )}

          {/* Institutional pre-check — no questionnaire for supervised entities */}
          {step === 'institutional' && (
            <div data-testid="lex-koller-modal-institutional" className="space-y-4">
              <div className="flex items-start gap-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50">
                <Landmark size={20} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    {t('lexKoller.institutional.title', 'Lex Koller ist nicht anwendbar')}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t(
                      'lexKoller.institutional.description',
                      '{{company}} ist als beaufsichtigte/registrierte Institution erfasst ({{source}}). Lex Koller ist damit nicht anwendbar.',
                      { company: institutionalCompany, source: institutionalSource },
                    )}
                  </p>
                </div>
              </div>

              <label className="flex items-start gap-3 text-sm text-gray-700 cursor-pointer">
                <input
                  data-testid="lex-koller-modal-no-foreign-control-checkbox"
                  type="checkbox"
                  checked={noForeignControl}
                  onChange={(e) => setNoForeignControl(e.target.checked)}
                  className="mt-0.5 rounded text-primary-600 focus:ring-primary-500"
                />
                <span>
                  {t(
                    'lexKoller.institutional.confirm',
                    'Die Gesellschaft ist nicht zu mehr als einem Drittel ausländisch beherrscht (Art. 5 BewG).',
                  )}
                </span>
              </label>
            </div>
          )}

          {/* Step: Entity Type */}
          {step === 'entity' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('lexKoller.entityType.title', 'Wer möchte erwerben?')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {t('lexKoller.entityType.description', 'Bitte wählen Sie, ob Sie als Privatperson oder als Firma kaufen möchten.')}
                </p>
              </div>
              <div className="space-y-3">
                <OptionCard
                  selected={entityType === ENTITY_TYPE.PERSON}
                  onClick={() => setEntityType(ENTITY_TYPE.PERSON)}
                  icon={<User size={20} />}
                  title={t('lexKoller.entityType.person', 'Privatperson')}
                  description={t('lexKoller.entityType.personDesc', 'Natürliche Person')}
                />
                <OptionCard
                  selected={entityType === ENTITY_TYPE.COMPANY}
                  onClick={() => setEntityType(ENTITY_TYPE.COMPANY)}
                  icon={<Building2 size={20} />}
                  title={t('lexKoller.entityType.company', 'Firma / Gesellschaft')}
                  description={t('lexKoller.entityType.companyDesc', 'AG, GmbH oder andere juristische Person')}
                />
              </div>
            </div>
          )}

          {/* Step: Nationality */}
          {step === 'nationality' && (
            <div className="space-y-4">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('lexKoller.nationality.title', 'Staatsangehörigkeit')}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {entityType === ENTITY_TYPE.COMPANY
                    ? t('lexKoller.nationality.companyDesc', 'Nationalität der wirtschaftlich Berechtigten.')
                    : t('lexKoller.nationality.personDesc', 'Wählen Sie Ihre Staatsangehörigkeit.')}
                </p>
                {nationalityFromKyc && (
                  <p
                    data-testid="lex-koller-modal-kyc-prefill-hint"
                    className="text-sm text-gray-500 mt-1"
                  >
                    {t(
                      'lexKoller.prefill.fromDocument',
                      'Aus Ihrem Ausweis übernommen — bitte prüfen und bei Bedarf ändern.',
                    )}
                  </p>
                )}
              </div>
              <div className="space-y-3">
                <OptionCard
                  selected={nationality === NATIONALITY_CATEGORY.CH}
                  onClick={() => setNationality(NATIONALITY_CATEGORY.CH)}
                  icon={<Globe size={20} />}
                  title={t('lexKoller.nationality.ch', 'Schweiz')}
                  description={t('lexKoller.nationality.chDesc', 'Schweizer Staatsbürger (inkl. Doppelbürger)')}
                />
                <OptionCard
                  selected={nationality === NATIONALITY_CATEGORY.EU_EFTA}
                  onClick={() => setNationality(NATIONALITY_CATEGORY.EU_EFTA)}
                  icon={<Globe size={20} />}
                  title={t('lexKoller.nationality.euEfta', 'EU / EFTA')}
                  description={t('lexKoller.nationality.euEftaDesc', 'EU- oder EFTA-Staatsangehörige')}
                />
                <OptionCard
                  selected={nationality === NATIONALITY_CATEGORY.OTHER}
                  onClick={() => setNationality(NATIONALITY_CATEGORY.OTHER)}
                  icon={<Globe size={20} />}
                  title={t('lexKoller.nationality.other', 'Anderes Land')}
                  description={t('lexKoller.nationality.otherDesc', 'Drittstaatsangehörige')}
                />
              </div>
            </div>
          )}

          {/* Step: Details (Person) */}
          {step === 'details' && entityType === ENTITY_TYPE.PERSON && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('lexKoller.details.title', 'Aufenthalt & Bewilligung')}
                </h3>
                {nationalityPrefilled && (
                  <p data-testid="lex-koller-modal-prefill-hint" className="text-sm text-gray-500 mt-1">
                    {t(
                      'lexKoller.prefill.fromIdent',
                      'Ihre Staatsangehörigkeit haben wir aus Ihrer Identifikation übernommen — wir fragen nur noch, was uns fehlt.',
                    )}
                  </p>
                )}
              </div>

              {/* Residence Permit */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <FileCheck size={14} className="inline mr-1" />
                  {t('lexKoller.details.permit', 'Aufenthaltsbewilligung')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { val: 'C' as ResidencePermit, label: t('lexKoller.details.permitC', 'C — Niederlassung') },
                    { val: 'B' as ResidencePermit, label: t('lexKoller.details.permitB', 'B — Aufenthalt') },
                    { val: 'G' as ResidencePermit, label: t('lexKoller.details.permitG', 'G — Grenzgänger') },
                    { val: 'L' as ResidencePermit, label: t('lexKoller.details.permitL', 'L — Kurzaufenthalt') },
                    { val: 'NONE' as ResidencePermit, label: t('lexKoller.details.noPermit', 'Keine') },
                  ]).map(({ val, label }) => (
                    <button
                      data-testid="lex-koller-modal-residence-permit-btn"
                      key={val}
                      type="button"
                      aria-pressed={residencePermit === val}
                      onClick={() => setResidencePermit(val)}
                      className={`px-3 py-2 text-sm rounded-lg border transition-all ${
                        residencePermit === val
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Swiss Residence */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <MapPin size={14} className="inline mr-1" />
                  {t('lexKoller.details.residence', 'Wohnsitz in der Schweiz?')}
                </label>
                <div className="flex gap-3">
                  <button
                    data-testid="lex-koller-modal-swiss-residence-btn-1"
                    type="button"
                    aria-pressed={swissResidence === true}
                    onClick={() => setSwissResidence(true)}
                    className={`flex-1 px-4 py-2 text-sm rounded-lg border transition-all ${
                      swissResidence === true
                        ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {t('common.yes', 'Ja')}
                  </button>
                  <button
                    data-testid="lex-koller-modal-swiss-residence-btn-2"
                    type="button"
                    aria-pressed={swissResidence === false}
                    onClick={() => { setSwissResidence(false); setSwissCanton('') }}
                    className={`flex-1 px-4 py-2 text-sm rounded-lg border transition-all ${
                      swissResidence === false
                        ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                        : 'border-gray-200 text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {t('common.no', 'Nein')}
                  </button>
                </div>
              </div>

              {/* Canton selector */}
              {swissResidence && (
                <div>
                  <label
                    htmlFor="lex-koller-canton"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    {t('lexKoller.details.canton', 'Kanton')}
                  </label>
                  <select
                    data-testid="lex-koller-modal-select"
                    id="lex-koller-canton"
                    value={swissCanton}
                    onChange={(e) => setSwissCanton(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="">{t('lexKoller.details.selectCanton', 'Kanton wählen...')}</option>
                    {SWISS_CANTONS.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* Step: Details (Company) */}
          {step === 'details' && entityType === ENTITY_TYPE.COMPANY && (
            <div className="space-y-5">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('lexKoller.company.title', 'Firmendaten')}
                </h3>
              </div>

              {/* Zefix already confirmed the Swiss domicile — no need to ask */}
              {domicilePrefilled && companyDomicileCh ? (
                <p
                  data-testid="lex-koller-modal-domicile-prefilled"
                  className="flex items-start gap-2 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3"
                >
                  <CheckCircle2 size={16} className="text-emerald-500 flex-shrink-0 mt-0.5" />
                  <span>
                    {t(
                      'lexKoller.company.domicilePrefilled',
                      'Der Firmensitz in der Schweiz ist über Zefix bestätigt.',
                    )}
                  </span>
                </p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('lexKoller.company.domicile', 'Firmensitz in der Schweiz?')}
                  </label>
                  <div className="flex gap-3">
                    <button
                      data-testid="lex-koller-modal-company-domicile-ch-btn-1"
                      type="button"
                      aria-pressed={companyDomicileCh === true}
                      onClick={() => setCompanyDomicileCh(true)}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border transition-all ${
                        companyDomicileCh === true
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {t('common.yes', 'Ja')}
                    </button>
                    <button
                      data-testid="lex-koller-modal-company-domicile-ch-btn-2"
                      type="button"
                      aria-pressed={companyDomicileCh === false}
                      onClick={() => setCompanyDomicileCh(false)}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border transition-all ${
                        companyDomicileCh === false
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {t('common.no', 'Nein')}
                    </button>
                  </div>
                </div>
              )}

              {companyDomicileCh && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('lexKoller.company.foreignOwnership', 'Ausländische Beteiligung über 33%?')}
                  </label>
                  <p className="text-xs text-gray-400 mb-2">
                    {t('lexKoller.company.foreignOwnershipDesc', 'Kapitalanteil oder Stimmrechte durch Personen im Ausland.')}
                  </p>
                  <div className="flex gap-3">
                    <button
                      data-testid="lex-koller-modal-foreign-ownership-btn-1"
                      type="button"
                      aria-pressed={foreignOwnership === true}
                      onClick={() => setForeignOwnership(true)}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border transition-all ${
                        foreignOwnership === true
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {t('common.yes', 'Ja')}
                    </button>
                    <button
                      data-testid="lex-koller-modal-foreign-ownership-btn-2"
                      type="button"
                      aria-pressed={foreignOwnership === false}
                      onClick={() => setForeignOwnership(false)}
                      className={`flex-1 px-4 py-2 text-sm rounded-lg border transition-all ${
                        foreignOwnership === false
                          ? 'border-primary-500 bg-primary-50 text-primary-700 font-medium'
                          : 'border-gray-200 text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      {t('common.no', 'Nein')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Result — institutional */}
          {step === 'result' && institutionalDone && (
            <div data-testid="lex-koller-modal-institutional-result" className="space-y-4">
              <div className={`p-6 rounded-xl border text-center ${statusColor(LEX_KOLLER_STATUS.NOT_APPLICABLE_INSTITUTIONAL)}`}>
                <div className="flex justify-center mb-3">
                  {statusIcon(LEX_KOLLER_STATUS.NOT_APPLICABLE_INSTITUTIONAL)}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {t('lexKoller.status.notApplicableInstitutional', 'Nicht anwendbar (institutionell)')}
                </h3>
                <p className="text-sm text-gray-600">
                  {t(
                    'lexKoller.institutional.doneBody',
                    'Ihre Bestätigung ist erfasst. Für {{company}} gelten keine Lex-Koller-Beschränkungen.',
                    { company: institutionalCompany },
                  )}
                </p>
              </div>
              <p className="text-xs text-gray-400 text-center">
                {t('lexKoller.disclaimer', 'Dies ist eine informative Einschätzung und keine Rechtsberatung. Für verbindliche Auskünfte wenden Sie sich an einen Notar oder Rechtsanwalt.')}
              </p>
            </div>
          )}

          {/* Result — assessment */}
          {step === 'result' && result && (
            <div className="space-y-4">
              <div className={`p-6 rounded-xl border text-center ${statusColor(result.status)}`}>
                <div className="flex justify-center mb-3">
                  {statusIcon(result.status)}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{result.summary}</h3>
              </div>

              {result.details.length > 0 && (
                <div className="space-y-2">
                  {result.details.map((d, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                      <span className="text-gray-400 mt-0.5">•</span>
                      <span>{d}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Capability summary */}
              <div className="grid grid-cols-1 gap-2 text-sm">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('lexKoller.result.primaryResidence', 'Hauptwohnung')}</span>
                  <span className={result.can_buy_primary_residence ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                    {result.can_buy_primary_residence ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('lexKoller.result.investmentProperty', 'Anlageobjekt')}</span>
                  <span className={result.can_buy_investment_property ? 'text-emerald-600 font-medium' : 'text-red-600 font-medium'}>
                    {result.can_buy_investment_property ? '✓' : '✗'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">{t('lexKoller.result.commercial', 'Geschäftsliegenschaft')}</span>
                  <span className="text-emerald-600 font-medium">✓</span>
                </div>
                {result.requires_cantonal_approval && (
                  <div className="flex items-center gap-2 py-2 text-amber-600">
                    <AlertTriangle size={14} />
                    <span className="text-sm">{t('lexKoller.result.cantonalApproval', 'Kantonale Bewilligung erforderlich')}</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-gray-400 text-center">
                {t('lexKoller.disclaimer', 'Dies ist eine informative Einschätzung und keine Rechtsberatung. Für verbindliche Auskünfte wenden Sie sich an einen Notar oder Rechtsanwalt.')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )

  // No footer while the pre-check is loading — the step is not decided yet
  const footer = statusLoading ? null : (
    <div
      className={`flex items-center justify-between gap-3 border-t border-gray-100 ${
        embedded ? 'pt-4 mt-5' : 'px-4 sm:px-6 py-4'
      }`}
    >
      {step === 'auto' ? (
        <button
          data-testid="lex-koller-modal-auto-edit-btn"
          type="button"
          onClick={() => setStep(startStep)}
          className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
        >
          {t('lexKoller.auto.edit', 'Angaben ändern')}
        </button>
      ) : step !== 'result' && previousStep() ? (
        <button
          data-testid="lex-koller-modal-go-back-btn"
          type="button"
          onClick={goBack}
          className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft size={16} />
          {t('common.back', 'Zurück')}
        </button>
      ) : step !== 'result' && onSkip ? (
        <button
          data-testid="lex-koller-modal-skip-btn"
          type="button"
          onClick={onSkip}
          className="text-sm text-gray-500 hover:text-gray-700 underline underline-offset-2"
        >
          {t('lexKoller.later', 'Später erledigen')}
        </button>
      ) : (
        <div />
      )}

      {step === 'result' || step === 'auto' ? (
        <button
          data-testid="lex-koller-modal-close-btn-2"
          type="button"
          onClick={onClose}
          className="px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
        >
          {t('common.close', 'Schliessen')}
        </button>
      ) : (
        <button
          data-testid="lex-koller-modal-go-next-btn"
          type="button"
          onClick={goNext}
          disabled={!canProceed() || loading}
          className="flex items-center gap-1 px-5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : step === 'institutional' && noForeignControl ? (
            t('lexKoller.institutional.submit', 'Bestätigen')
          ) : (
            <>
              {t('common.next', 'Weiter')}
              <ChevronRight size={16} />
            </>
          )}
        </button>
      )}
    </div>
  )

  if (embedded) {
    return (
      <div data-testid="lex-koller-embedded">
        {body}
        {footer}
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lex-koller-modal-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2 min-w-0">
            <Shield className="h-5 w-5 text-primary-600 flex-shrink-0" />
            <h2 id="lex-koller-modal-title" className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              {t('lexKoller.title', 'Lex Koller Einschätzung')}
            </h2>
          </div>
          <button
            data-testid="lex-koller-modal-close-btn-1"
            type="button"
            onClick={onClose}
            aria-label={t('common.close', 'Schliessen')}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 flex-shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {body}
        {footer}
      </div>
    </div>
  )
}
